// The scale catalog's generator: one level per playable scale unit, holding the
// unit's twelve tonic-cells, with the exercise's three tiers and one package per
// tier. Pure: the same template always mints the same content, so re-running it
// (and re-emitting the dump) changes nothing.
//
// What a unit's content is, beyond its formula:
// - Level: number from SCALE_LEVEL_NUMBERS (permanent), title = the unit's
//   catalog name, cells = the twelve tonics in circle-of-fifths order.
// - Tiers: the exercise's waypoint set, the same for every unit, hands together
//   at every tier, each with its gauntlet order (fixed, fixed, shuffled).
// - Packages: a stepwise run over the unit's own step pattern, in the
//   exercise's register. The meter is a layout tool and hidden: one bar holds
//   one ascending octave in quarters (the scale's notes plus the top tonic), so
//   seven-note scales sit in 8/4, pentatonics in 6/4, chromatic in 13/4.
//   Package keys are `stepwise-run-<slug>-t<tier>`.
//
// Major (level 1) is not minted here: its level and packages predate the
// catalog and keep their own keys (see scalesPerformanceRead.ts).

import type { Grading, ScaleCell } from 'Types/ExerciseContentTypes';
import type { RegisterSpec, StepwiseRunPrompt } from 'Types/ExerciseRecipeTypes';
import { scaleFormula, scaleStepIntervals } from './scaleFormulas';
import { PLAYABLE_SCALE_UNIT_IDS, SCALE_LEVEL_NUMBERS, scaleSlugForUnit } from './scaleLevels';
import type { ScaleSlug } from './scaleLevels';
import { CIRCLE_OF_FIFTHS_TONICS, SCALE_UNITS } from './scaleLibraryCatalog';
import type {
  SeedExerciseLevel,
  SeedExercisePackage,
  SeedExerciseTier,
  SeedTierWaypoint,
} from './seedTypes';

export type TierNumber = 1 | 2 | 3;

export const TIER_NUMBERS: readonly TierNumber[] = [1, 2, 3];

/** What every unit shares: the exercise's tiers, register and grading. */
export interface ScaleContentTemplate {
  waypoints: Record<TierNumber, SeedTierWaypoint>;
  openingBpm: Record<TierNumber, number>;
  register: RegisterSpec;
  grading: Grading;
}

export interface ScaleCatalogContent {
  levels: SeedExerciseLevel[];
  packages: SeedExercisePackage[];
}

/** The units the generator mints: every playable unit but major. */
export const GENERATED_SCALE_UNIT_IDS: readonly string[] = PLAYABLE_SCALE_UNIT_IDS.filter(
  (unitId) => scaleSlugForUnit(unitId) !== 'major',
);

export const scalePackageKey = (slug: ScaleSlug, tier: TierNumber): string =>
  `stepwise-run-${slug}-t${tier}`;

/** One bar = one ascending octave in quarters: the scale's notes plus the top tonic. */
export const scaleRunMeter = (unitId: string): [number, number] => [
  scaleFormula(unitId).length + 1,
  4,
];

const runPrompt = (unitId: string, register: RegisterSpec): StepwiseRunPrompt => ({
  figure: 'stepwise_run',
  intervals: scaleStepIntervals(unitId),
  time_signature: scaleRunMeter(unitId),
  show_time_signature: false,
  register,
  traversal: { kind: 'single_cell' },
});

const firstName = (unitId: string): string => SCALE_UNITS[unitId].name.split(' / ')[0];

const unitLevel = (unitId: string, template: ScaleContentTemplate): SeedExerciseLevel => {
  const slug = scaleSlugForUnit(unitId);
  const cells: ScaleCell[] = CIRCLE_OF_FIFTHS_TONICS.map((tonic) => ({ scale: slug, tonic }));
  const tiers: SeedExerciseTier[] = TIER_NUMBERS.map((tier) => ({
    tier,
    waypoint_config: template.waypoints[tier],
    package_key: scalePackageKey(slug, tier),
  }));
  return {
    level: SCALE_LEVEL_NUMBERS[slug],
    title: SCALE_UNITS[unitId].name,
    cell_scope: cells,
    is_review: false,
    description: `The ${firstName(unitId)} scale in all twelve keys.`,
    tiers,
  };
};

const unitPackages = (unitId: string, template: ScaleContentTemplate): SeedExercisePackage[] => {
  const slug = scaleSlugForUnit(unitId);
  const prompt = runPrompt(unitId, template.register);
  return TIER_NUMBERS.map((tier) => ({
    key: scalePackageKey(slug, tier),
    prompt,
    interaction_family: 'performed',
    scene_config: null,
    grading: template.grading,
    defaults: { bpm: template.openingBpm[tier] },
  }));
};

/** Mint the catalog's levels (in level-number order) and their packages. */
export const generateScaleCatalogContent = (
  template: ScaleContentTemplate,
): ScaleCatalogContent => {
  const unitIds = [...GENERATED_SCALE_UNIT_IDS].sort(
    (a, b) => SCALE_LEVEL_NUMBERS[scaleSlugForUnit(a)] - SCALE_LEVEL_NUMBERS[scaleSlugForUnit(b)],
  );
  return {
    levels: unitIds.map((unitId) => unitLevel(unitId, template)),
    packages: unitIds.flatMap((unitId) => unitPackages(unitId, template)),
  };
};
